import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { deleteJSON, postJSON, putJSON } from 'api/fetch';
import { RootState } from 'initializers/types';

import { HIRING_V2_SLICE_NAME } from '../constants';
import { createReducers, createSelectors } from '../helpers';
import { JobRequest, JobRequestAPIResponse } from '../interfaces/IJobRequest';
import { getJobRequests } from '../slices/jobRequests/slice';

import { initialJobData } from './constants';
import { mapJobRequestToJobData } from './helpers';
import { JobDescriptionDataState, JobDescriptionState } from './types';

const SLICE_NAME = 'jobDescription';

export const INITIAL_JOB_DESCRIPTION_DATA_STATE: JobDescriptionDataState = {
  jobDescription: initialJobData,
  postedJob: null,
};

export const INITIAL_JOB_DESCRIPTION_STATE: JobDescriptionState = {
  ...INITIAL_JOB_DESCRIPTION_DATA_STATE,
  jobDescriptionLoading: false,
  jobDescriptionError: null,
  postedJobLoading: false,
  postedJobError: null,
};

const initialState = INITIAL_JOB_DESCRIPTION_STATE;

const jobDescriptionSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: createReducers(initialState),
});

const getJobDescriptionState = (state: RootState) =>
  state.get(HIRING_V2_SLICE_NAME).jobDescription as JobDescriptionState;

// Selectors
export const selectJobDescription = createSelectors(
  getJobDescriptionState,
  initialState
);
export const { reducer, actions } = jobDescriptionSlice;

interface ApiError {
  message: string;
}

// THUNKS
export const saveJobDescription = createAsyncThunk(
  `${SLICE_NAME}/saveJobDescription`,
  async (
    data: { jobRequest: JobRequest; updateState?: boolean },
    { dispatch }
  ) => {
    if (!data.jobRequest.id) return;
    try {
      dispatch(actions.setJobDescriptionLoading(true));
      const response = (await putJSON(
        `/hiring/v2/job_requests/${data.jobRequest.id}`,
        {
          job_request: data.jobRequest,
        }
      )) as JobRequestAPIResponse;
      const jobDataFromResponse = mapJobRequestToJobData(response.job_request);
      if (data.updateState) {
        dispatch(actions.setJobDescription(jobDataFromResponse));
      }
      dispatch(actions.setJobDescriptionError(null));
      return jobDataFromResponse;
    } catch (err) {
      const error = err as ApiError;
      dispatch(actions.setJobDescriptionError(error.message));
      throw error;
    } finally {
      dispatch(actions.setJobDescriptionLoading(false));
    }
  }
);

export const resetJobDescription = createAsyncThunk(
  `${SLICE_NAME}/resetJobDescription`,
  async (_, { dispatch }) => {
    dispatch(actions.setJobDescription(initialJobData));
  }
);

export const postJobDescription = createAsyncThunk(
  `${SLICE_NAME}/postJobDescription`,
  async (data: JobRequest, { dispatch }) => {
    try {
      dispatch(actions.setPostedJobLoading(true));
      if (!data.id) {
        const error = new Error('Job ID is required');
        dispatch(actions.setPostedJobError(error.message));
        throw error;
      }
      const response = (await postJSON(
        `/hiring/v2/job_requests/${data.id}/post_job`,
        {}
      )) as JobRequestAPIResponse;
      dispatch(actions.setPostedJob(response.job_request));
      dispatch(actions.setPostedJobError(null));
      dispatch(getJobRequests()); // Refresh job requests
    } catch (err) {
      const error = err as ApiError;
      dispatch(actions.setPostedJobError(error.message));
      throw error;
    } finally {
      dispatch(actions.setPostedJobLoading(false));
    }
  }
);

export const updateJobDescription = createAsyncThunk(
  `${SLICE_NAME}/updateJobDescription`,
  async (data: JobRequest, { dispatch }) => {
    try {
      dispatch(actions.setPostedJobLoading(true));
      if (!data.id) {
        const error = new Error('Job ID is required');
        dispatch(actions.setPostedJobError(error.message));
        throw error;
      }
      const response = (await putJSON(
        `/hiring/v2/job_requests/${data.id}/post_job`,
        { job_request: data }
      )) as JobRequestAPIResponse;
      dispatch(actions.setPostedJob(response.job_request));
      dispatch(actions.setPostedJobError(null));
      dispatch(getJobRequests()); // Refresh job requests
    } catch (err) {
      const error = err as ApiError;
      dispatch(actions.setPostedJobError(error.message));
      throw error;
    } finally {
      dispatch(actions.setPostedJobLoading(false));
    }
  }
);

export const createEqualOpportunityQuestions = createAsyncThunk(
  `${SLICE_NAME}/createEqualOpportunityQuestions`,
  async (data: { id: number; questions: string[] }) => {
    try {
      const response = (await postJSON(
        `/hiring/v2/job_requests/${data.id}/application_questions/bulk_create`,
        {
          application_questions: {
            question_type: 'equal_employment_opportunity',
            questions: data.questions.map(q => ({ text: q })),
          },
        }
      )) as {
        application_questions: {
          question_type: string;
          questions: { text: string }[];
        };
      };
      return response;
    } catch (err) {
      const error = err as ApiError;
      throw error;
    }
  }
);

export const deleteEqualOpportunityQuestions = createAsyncThunk(
  `${SLICE_NAME}/deleteEqualOpportunityQuestions`,
  async (data: { id: number; ids: number[] }) => {
    try {
      const response = (await deleteJSON(
        `/hiring/v2/job_requests/${data.id}/application_questions/bulk_delete`,
        { application_questions: { ids: data.ids } }
      )) as { success: boolean };
      return response;
    } catch (err) {
      const error = err as ApiError;
      throw error;
    }
  }
);
